/**
 * What the pilot can say about another aircraft: how far, which way, above or
 * below. This is the HUD's view of a contact, and deliberately *not*
 * `relativeGeometry` — that one works in the body frame, so a target dead ahead
 * reads as "off to the left" the moment the aircraft banks. A bearing callout
 * has to be horizon-referenced, like a compass and a clock face.
 */

import { headingAngle, lengthVec3 } from "../flight/math3d.ts";
import type { SimState } from "../flight/state.ts";
import { relativeGeometry, subtractVec3, bodyOf } from "./geometry.ts";

export interface ContactReport {
  readonly id: string;
  readonly rangeM: number;
  /** Compass bearing to the contact: 0 = north, increasing clockwise. */
  readonly bearingRad: number;
  /** Bearing relative to where the observer's nose points, in (−π, π]; 0 = ahead. */
  readonly relativeBearingRad: number;
  /** Contact's altitude minus the observer's. */
  readonly heightDifferenceM: number;
  /** Positive when the gap is closing. */
  readonly closureMps: number;
  /** True when the contact is behind the observer's 3/9 line. */
  readonly behind: boolean;
  /** 1..12 clock position, the way a WWI pilot would call it ("bandit at 6 o'clock"). */
  readonly clockPosition: number;
}

function wrapSigned(radians: number): number {
  const wrapped = (radians + Math.PI) % (2 * Math.PI);
  return (wrapped < 0 ? wrapped + 2 * Math.PI : wrapped) - Math.PI;
}

/** 12 for dead ahead, 3 to the right, 6 astern, 9 to the left. */
export function clockPositionOf(relativeBearingRad: number): number {
  const twelfths = Math.round((wrapSigned(relativeBearingRad) / (2 * Math.PI)) * 12);
  const position = ((twelfths % 12) + 12) % 12;
  return position === 0 ? 12 : position;
}

export function contactReport(id: string, observer: SimState, contact: SimState): ContactReport {
  const offset = subtractVec3(contact.position, observer.position);
  const bearingRad = Math.atan2(offset.x, -offset.z);
  const relativeBearingRad = wrapSigned(bearingRad - headingAngle(observer.orientation));
  const geometry = relativeGeometry(bodyOf(observer), bodyOf(contact));
  return {
    id,
    rangeM: lengthVec3(offset),
    bearingRad,
    relativeBearingRad,
    heightDifferenceM: offset.y,
    closureMps: geometry.closureMps,
    behind: Math.abs(relativeBearingRad) > Math.PI / 2,
    clockPosition: clockPositionOf(relativeBearingRad),
  };
}
